import React, { useEffect, useState } from 'react';
import GlobalTable from '../components/GlobalTable';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';

const USAGE_TYPES = [
    { label: 'Invoice (Receipt)', value: 'INVOICE' },
    { label: 'Label', value: 'LABEL' },
    { label: 'Barcode', value: 'BARCODE' },
    { label: 'Report', value: 'REPORT' },
];

const LANGUAGE_OPTIONS = [
    { label: 'ESC/POS (Receipt)', value: 'ESC_POS' },
    { label: 'TSPL (Label)', value: 'TSPL' },
];

const PrinterAssignmentsPage = () => {
    const [rows, setRows] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [editing, setEditing] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [a, p, d] = await Promise.all([
                axiosInstance.get('/api/printer-assignments/'),
                axiosInstance.get('/api/printers/'),
                axiosInstance.get('/api/departments/'),
            ]);

            setRows(a.data || []);
            setPrinters(p.data || []);
            setDepartments(d.data || []);
        } catch (e) {
            handleApiError(e);
        } finally {
            setLoading(false);
        }
    };

    const openNew = () => {
        setEditing({
            printer: null,
            department: null,
            usage_type: null,
            required_language: null,
            is_default: false,
        });
        setDialogVisible(true);
    };

    const saveAssignment = async () => {
        try {
            if (editing.id) {
                await axiosInstance.patch(
                    `/api/printer-assignments/${editing.id}/`,
                    editing
                );
                handleApiSuccess('Assignment updated');
            } else {
                await axiosInstance.post(
                    '/api/printer-assignments/',
                    editing
                );
                handleApiSuccess('Assignment created');
            }
            setDialogVisible(false);
            loadData();
        } catch (e) {
            handleApiError(e);
        }
    };

    /* ---------------- FILTER PRINTERS BY CAPABILITY ---------------- */

    const getFilteredPrinters = () => {
        if (!editing?.usage_type) return printers;

        if (editing.usage_type === 'INVOICE' || editing.usage_type === 'REPORT') {
            return printers.filter(p => p.can_print_receipt);
        }

        if (editing.usage_type === 'LABEL') {
            return printers.filter(p => p.can_print_label);
        }

        if (editing.usage_type === 'BARCODE') {
            return printers.filter(p => p.can_print_barcode);
        }

        return printers;
    };

    const columns = [
        {
            Header: 'Usage',
            accessor: 'usage_type',
            sortable: true,
            filtered: true,
        },
        {
            Header: 'Department',
            accessor: 'department',
            sortable: true,
            filtered: true,
            bodyTemplate: (row) =>
                row.department_detail
                    ? row.department_detail.department
                    : 'ORG',
        },
        {
            Header: 'Printer',
            accessor: 'printer',
            sortable: true,
            filtered: true,
            bodyTemplate: (row) =>
                row.printer_detail
                    ? row.printer_detail.name
                    : '',
        },
        {
            Header: 'Language',
            accessor: 'required_language',
            sortable: true,
            filtered: true,
        },
        {
            Header: 'Default',
            accessor: 'is_default',
            sortable: true,
            filtered: true,
            bodyTemplate: (row) => (
                                <Checkbox checked={row.is_default === true || row.is_default === 'Y'} disabled />
                            ),
        },
    ];


    const onRowEditComplete = async (e) => {
        const { newData } = e;

        try {
            await axiosInstance.patch(
                `/api/printer-assignments/${newData.id}/`,
                {
                    printer: newData.printer,
                    department: newData.department,
                    usage_type: newData.usage_type,
                    required_language: newData.required_language,
                    is_default: newData.is_default,
                }
            );
            handleApiSuccess('Printer assignment updated');
            loadData();
        } catch (err) {
            handleApiError(err);
        }
    };

    return (
        <div>
            <div className='containers'>
            <GlobalTable
                columns={columns}
                data={rows}
                tabletitle="Printer Assignments"
                loading={loading}
                editMode="row"
                onRowEditComplete={onRowEditComplete}
                headerAction={<Button icon="pi pi-plus" label="Add Assignment" onClick={() => openNew ()} /> }
                customEditors = {{
                    usage_type: (options) => (
                        <Dropdown
                            value={options.rowData.usage_type}
                            options={USAGE_TYPES}
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.usage_type = e.value)
                                )
                            }
                        />
                    ),
                    department: (options) => (
                        <Dropdown
                            value={options.rowData.department}
                            options={departments}
                            optionLabel="department"
                            optionValue="id"
                            showClear
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.department = e.value)
                                )
                            }
                        />
                    ),
                    printer: (options) => (
                        <Dropdown
                            value={options.rowData.printer}
                            options={printers}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.printer = e.value)
                                )
                            }
                        />
                    ),
                    required_language: (options) => (
                        <Dropdown
                            value={options.rowData.required_language}
                            options={LANGUAGE_OPTIONS}
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.required_language = e.value)
                                )
                            }
                        />
                    ),
                    is_default: (options) => (
                        <Dropdown
                            value={options.rowData.is_default}
                            options={[
                                { label: 'Yes (Default)', value: true },
                                { label: 'No', value: false },
                            ]}
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.is_default = e.value)
                                )
                            }
                        />
                    ),
                }}

            />

            <Dialog
                header={'New Printer Assignment'}
                visible={dialogVisible}
                style={{ width: '35rem' }}
                modal
                onHide={() => setDialogVisible(false)}
            >
                {editing && (
                    <div className="p-fluid">

                        <div className="field">
                            <label>Usage Type</label>
                            <Dropdown
                                value={editing.usage_type}
                                options={USAGE_TYPES}
                                onChange={(e) =>
                                    setEditing({
                                        ...editing,
                                        usage_type: e.value,
                                        printer: null,
                                        required_language: null
                                    })
                                }
                                placeholder="Select usage"
                            />
                        </div>

                        <div className="field">
                            <label>Department</label>
                            <Dropdown
                                value={editing.department}
                                options={departments}
                                optionLabel="department"
                                optionValue="id"
                                onChange={(e) =>
                                    setEditing({ ...editing, department: e.value })
                                }
                                placeholder="Organization level"
                                showClear
                            />
                        </div>

                        <div className="field">
                            <label>Printer</label>
                            <Dropdown
                                value={editing.printer}
                                options={getFilteredPrinters()}
                                optionLabel="name"
                                optionValue="id"
                                onChange={(e) =>
                                    setEditing({ ...editing, printer: e.value })
                                }
                                placeholder="Select printer"
                            />
                        </div>

                        <div className="field">
                            <label>Required Language</label>
                            <Dropdown
                                value={editing.required_language}
                                options={LANGUAGE_OPTIONS}
                                onChange={(e) =>
                                    setEditing({ ...editing, required_language: e.value })
                                }
                                placeholder="Select language"
                            />
                        </div>

                        <div className="field-checkbox mt-2">
                            <Checkbox
                                checked={editing.is_default}
                                onChange={(e) =>
                                    setEditing({ ...editing, is_default: e.checked })
                                }
                            />
                            <label>Default printer for this usage</label>
                        </div>

                        <div className="flex justify-content-end mt-4">
                            <Button
                                label="Save"
                                icon="pi pi-save"
                                onClick={saveAssignment}
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
        </div>
    );
};

export default PrinterAssignmentsPage;
